/**
 * Iter 222 · Public Ambulance Portal
 * A citizen-facing single-page app at /amb-public. Phone-OTP sign-up,
 * emergency profile, live map (privacy-rounded to 300m grid), one-tap
 * SOS, personal mission history.
 */
import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Siren, Ambulance, User, Phone, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE = "amb_public_token";

async function apiCall(path, opts = {}) {
  const token = localStorage.getItem(STORAGE);
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const r = await fetch(`${API}/api${path}`, { ...opts, headers });
  const text = await r.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: text }; }
  if (!r.ok) throw new Error(data?.detail || data?.error || r.statusText);
  return data;
}

export default function AmbulancePublicPortal() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem(STORAGE);
    if (!token) { setLoading(false); return; }
    apiCall("/ambulance/public/me")
      .then(r => setMe(r.citizen))
      .catch(() => localStorage.removeItem(STORAGE))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!me) return <AuthFlow onDone={(c) => setMe(c)}/>;
  return <PortalHome me={me} onLogout={() => { localStorage.removeItem(STORAGE); setMe(null); }}/>;
}

function AuthFlow({ onDone }) {
  const [step, setStep] = useState("phone");   // phone → otp → profile
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [profile, setProfile] = useState({
    name:"", blood_group:"", allergies:"", conditions:"",
    ice_name:"", ice_phone:"", home_address:"",
  });

  const sendOtp = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await apiCall("/ambulance/public/register/otp", {
        method:"POST", body: JSON.stringify({ phone }),
      });
      setDevOtp(r.dev_otp || "");
      setStep("otp");
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const verifyExisting = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await apiCall("/ambulance/public/login/verify", {
        method:"POST", body: JSON.stringify({ phone, otp }),
      });
      localStorage.setItem(STORAGE, r.token);
      onDone(r.citizen);
    } catch {
      // Not registered yet — go to profile
      setStep("profile");
    } finally { setBusy(false); }
  };

  const register = async () => {
    setErr(""); setBusy(true);
    try {
      const body = {
        name: profile.name, phone,
        blood_group: profile.blood_group || null,
        allergies: profile.allergies ? profile.allergies.split(",").map(s => s.trim()).filter(Boolean) : [],
        conditions: profile.conditions ? profile.conditions.split(",").map(s => s.trim()).filter(Boolean) : [],
        ice_contacts: profile.ice_name ? [{ name: profile.ice_name, phone: profile.ice_phone, relation: "" }] : [],
        home_address: profile.home_address || null,
      };
      const r = await apiCall("/ambulance/public/register", {
        method:"POST", body: JSON.stringify(body),
        headers: { "otp": otp, "challenge_phone": phone },
      });
      localStorage.setItem(STORAGE, r.token);
      const meResp = await apiCall("/ambulance/public/me");
      onDone(meResp.citizen);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FEE2E2] via-[#FFF7ED] to-[#DBEAFE] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-12 h-12 rounded-full bg-[#B91C1C] flex items-center justify-center">
            <Ambulance size={28} color="white" weight="fill"/>
          </div>
          <div>
            <div className="font-heading text-2xl text-[#1A1A1A]">Rescigo AmbulanceQ</div>
            <div className="text-xs text-[#5C5A55]">Public citizen portal</div>
          </div>
        </div>

        {err && <div className="mb-3 p-2 rounded bg-[#FEE2E2] text-[#B91C1C] text-sm">{err}</div>}

        {step === "phone" && (
          <div data-testid="amb-pub-phone-step" className="space-y-3">
            <label className="block">
              <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-1">Phone</div>
              <input data-testid="amb-pub-phone" value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="10-digit mobile"
                    className="w-full border border-[#E5E2D9] rounded-md px-3 py-2.5"/>
            </label>
            <button data-testid="amb-pub-send-otp" disabled={busy || phone.length < 10} onClick={sendOtp}
                    className="w-full py-2.5 rounded-md bg-[#B91C1C] text-white font-medium disabled:opacity-50">
              {busy ? "Sending…" : "Get OTP"}
            </button>
            <p className="text-[11px] text-[#8A8780] text-center">
              We use your phone only for emergency dispatch. No spam, no marketing.
            </p>
          </div>
        )}

        {step === "otp" && (
          <div data-testid="amb-pub-otp-step" className="space-y-3">
            <div className="text-sm text-[#5C5A55]">OTP sent to <b>{phone}</b>{devOtp && <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#B45309]">dev: {devOtp}</span>}</div>
            <input data-testid="amb-pub-otp" value={otp} onChange={e => setOtp(e.target.value)}
                    placeholder="6-digit OTP"
                    className="w-full border border-[#E5E2D9] rounded-md px-3 py-2.5"/>
            <button data-testid="amb-pub-verify" disabled={busy || otp.length < 4} onClick={verifyExisting}
                    className="w-full py-2.5 rounded-md bg-[#B91C1C] text-white font-medium disabled:opacity-50">
              {busy ? "Verifying…" : "Verify OTP"}
            </button>
            <button onClick={() => setStep("phone")} className="w-full text-sm text-[#5C5A55] py-1">Use a different number</button>
          </div>
        )}

        {step === "profile" && (
          <div data-testid="amb-pub-profile-step" className="space-y-2">
            <div className="text-sm text-[#5C5A55] mb-2">First time? Please set your emergency profile.</div>
            <input placeholder="Full name" data-testid="amb-pub-name"
                    value={profile.name} onChange={e => setProfile({...profile, name:e.target.value})}
                    className="w-full border rounded-md px-3 py-2"/>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Blood group" value={profile.blood_group}
                      onChange={e => setProfile({...profile, blood_group:e.target.value})}
                      className="border rounded-md px-3 py-2"/>
              <input placeholder="Allergies (comma sep.)" value={profile.allergies}
                      onChange={e => setProfile({...profile, allergies:e.target.value})}
                      className="border rounded-md px-3 py-2"/>
            </div>
            <input placeholder="Chronic conditions (comma sep.)" value={profile.conditions}
                    onChange={e => setProfile({...profile, conditions:e.target.value})}
                    className="w-full border rounded-md px-3 py-2"/>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="ICE contact name" value={profile.ice_name}
                      onChange={e => setProfile({...profile, ice_name:e.target.value})}
                      className="border rounded-md px-3 py-2"/>
              <input placeholder="ICE contact phone" value={profile.ice_phone}
                      onChange={e => setProfile({...profile, ice_phone:e.target.value})}
                      className="border rounded-md px-3 py-2"/>
            </div>
            <input placeholder="Home address" value={profile.home_address}
                    onChange={e => setProfile({...profile, home_address:e.target.value})}
                    className="w-full border rounded-md px-3 py-2"/>
            <button data-testid="amb-pub-register" disabled={busy || !profile.name} onClick={register}
                    className="w-full py-2.5 rounded-md bg-[#B91C1C] text-white font-medium disabled:opacity-50 mt-2">
              {busy ? "Saving…" : "Complete registration"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PortalHome({ me, onLogout }) {
  const [nearby, setNearby] = useState(null);
  const [pos, setPos] = useState(null);
  const [missions, setMissions] = useState([]);
  const [sosBusy, setSosBusy] = useState(false);
  const [sosSuccess, setSosSuccess] = useState(null);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      p => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setPos({ lat: 8.5241, lng: 76.9366 }),
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 10000 },
    );
    apiCall("/ambulance/public/my-missions").then(r => setMissions(r.missions || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!pos) return;
    const load = () => apiCall(`/ambulance/public/nearby?lat=${pos.lat}&lng=${pos.lng}`)
      .then(setNearby).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [pos]);

  const raiseSos = async () => {
    if (!pos) return;
    setSosBusy(true);
    try {
      const r = await apiCall("/ambulance/public/sos", {
        method: "POST", body: JSON.stringify({
          caller_name: me.name, caller_phone: me.phone,
          pickup_lat: pos.lat, pickup_lng: pos.lng,
          address: me.home_address || "",
          urgency: "emergency", amb_type_pref: "bls",
          complaint: "Public SOS",
        }),
      });
      setSosSuccess(r);
      const mr = await apiCall("/ambulance/public/my-missions");
      setMissions(mr.missions || []);
    } catch (e) { alert(e.message); }
    finally { setSosBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="amb-public-portal">
      <header className="bg-white border-b border-[#E5E2D9] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#B91C1C] flex items-center justify-center">
            <Ambulance size={20} color="white" weight="fill"/>
          </div>
          <div>
            <div className="font-heading text-lg">Rescigo AmbulanceQ</div>
            <div className="text-[11px] text-[#8A8780]">{me.name} · {me.phone}</div>
          </div>
        </div>
        <button onClick={onLogout} data-testid="amb-pub-logout"
                className="text-sm text-[#5C5A55] hover:text-[#B91C1C]">Sign out</button>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        {/* SOS block */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-[#B91C1C] to-[#7F1D1D] text-white shadow-xl">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider opacity-90">Emergency SOS</div>
              <div className="font-heading text-2xl mt-1">One tap dispatches the nearest ambulance to your live location.</div>
              <div className="text-xs opacity-80 mt-2">
                Auto-shares your emergency profile · blood group · allergies · ICE contacts.
              </div>
            </div>
            <button onClick={raiseSos} disabled={sosBusy || !pos} data-testid="amb-pub-sos"
                className="px-8 py-4 rounded-full bg-white text-[#B91C1C] font-bold text-lg shadow-md disabled:opacity-70 flex items-center gap-2">
              <Siren size={22} weight="fill"/> {sosBusy ? "Sending…" : "SEND SOS"}
            </button>
          </div>
          {sosSuccess && (
            <div className="mt-4 p-3 rounded-md bg-white/20 text-sm" data-testid="amb-pub-sos-success">
              <CheckCircle size={16} weight="fill" className="inline mr-1"/>
              SOS raised · Mission <b>{sosSuccess.mission_no}</b> — dispatch team is finding the nearest ambulance.
            </div>
          )}
        </div>

        {/* Live map */}
        <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between border-b border-[#E5E2D9]">
            <div>
              <div className="font-medium">Nearby ambulances</div>
              <div className="text-[11px] text-[#8A8780]">
                Precision: {nearby?.precision_m}m grid · {nearby?.count ?? 0} within 10 km
              </div>
            </div>
          </div>
          <div style={{ height: 380 }}>
            {pos && (
              <MapContainer center={[pos.lat, pos.lng]} zoom={13} style={{height:"100%", width:"100%"}}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                <CircleMarker center={[pos.lat, pos.lng]} radius={8} color="#4338CA" fillColor="#818CF8" fillOpacity={0.9}>
                  <Popup>Your location</Popup>
                </CircleMarker>
                {(nearby?.ambulances || []).map((a, i) => (
                  <CircleMarker key={i} center={[a.lat, a.lng]} radius={10} color="#B91C1C" fillColor="#FEE2E2" fillOpacity={0.7}>
                    <Popup>
                      {a.amb_type?.toUpperCase()} · {a.status?.replace(/_/g," ")} · ~{a.eta_min} min
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            )}
          </div>
        </div>

        {/* My missions */}
        <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
          <div className="font-medium mb-3">My ambulance history</div>
          {missions.length === 0 ? (
            <div className="text-sm text-[#8A8780]">No missions yet</div>
          ) : missions.map(m => (
            <div key={m.id} data-testid={`pub-mission-${m.mission_no}`}
                  className="flex items-center justify-between p-3 rounded border border-[#E5E2D9] mb-2">
              <div>
                <div className="font-mono text-[11px] text-[#8A8780]">{m.mission_no}</div>
                <div className="text-sm">{m.address || "—"}</div>
                <div className="text-[11px] text-[#8A8780]">{new Date(m.created_at).toLocaleString()}</div>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#F5F3EE] text-[#5C5A55]">{m.status?.replace(/_/g," ")}</span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
